import { useState } from 'react';
import { spreadFactors } from '../data';

const impactColorMap = ['', 'accent', 'primary', 'secondary', 'foreground'] as const;

type ColorKey = 'accent' | 'primary' | 'secondary' | 'foreground' | '';

const colorClass = (color: ColorKey, type: 'text' | 'bg') => {
  const map: Record<string, string> = {
    'accent-text': 'text-accent-600',
    'primary-text': 'text-primary-600',
    'secondary-text': 'text-secondary-600',
    'foreground-text': 'text-foreground-600',
    'accent-bg': 'bg-accent-500',
    'primary-bg': 'bg-primary-500',
    'secondary-bg': 'bg-secondary-500',
    'foreground-bg': 'bg-foreground-500',
  };
  return map[`${color}-${type}`] ?? 'text-foreground-600';
};

const statColorClass = (color: ColorKey) => {
  const map: Record<string, string> = {
    accent: 'text-accent-500',
    primary: 'text-primary-500',
    secondary: 'text-secondary-500',
    foreground: 'text-foreground-500',
  };
  return map[color] ?? 'text-foreground-500';
};

export default function SpreadFactorsSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const handleToggle = (i: number) => {
    setOpenIndex((prev) => (prev === i ? null : i));
  };

  return (
    <section id="spread-factors" className="w-full bg-background-100 py-12 md:py-18">
      <div className="max-w-main mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="max-w-[720px] mb-12 md:mb-16">
          <h2 className="text-[32px] md:text-[42px] font-[750] leading-[1.15] text-foreground-950 tracking-tight mb-4">
            Every spread you see is a story
            <br />
            — told by these four forces
          </h2>
          <p className="text-[15px] leading-relaxed text-foreground-600">
            A spread isn't a random number. It's a real-time negotiation between volatility, liquidity, timezone, and your broker's business model. Understand what's pushing it, and you control your entry, not the other way around.
          </p>
        </div>

        {/* Force strips */}
        <div className="bg-white rounded-card border border-background-200 overflow-hidden">
          {spreadFactors.map((factor, i) => {
            const barColor = impactColorMap[Math.min(factor.impactLevel, 4)];
            const isOpen = openIndex === i;

            const ImpactMeter = () => (
              <div className="flex flex-col items-center gap-0.5 pt-0.5">
                {[5, 4, 3, 2, 1].map((level) => {
                  const filled = level <= factor.impactLevel;
                  const segColor = level <= 2 ? 'foreground' : barColor;
                  return (
                    <span
                      key={level}
                      className={`block w-[3px] h-[5px] rounded-full ${
                        filled ? colorClass(segColor as ColorKey, 'bg') : 'bg-background-200'
                      }`}
                    />
                  );
                })}
              </div>
            );

            return (
              <div
                key={factor.title}
                className={`${i < spreadFactors.length - 1 ? 'border-b border-background-200' : ''}`}
              >
                {/* ── Desktop row: always fully expanded ───────────────── */}
                <div className="hidden md:block group px-8 py-7 hover:bg-background-50/60 transition-colors duration-200">
                  <div className="flex gap-6">
                    {/* Left: rank + impact bar */}
                    <div className="flex items-start gap-3 flex-shrink-0">
                      <ImpactMeter />
                      <span className="text-[34px] font-[800] text-foreground-200 leading-none tracking-tighter select-none">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                    </div>
                    {/* Main content */}
                    <div className="flex-1 min-w-0 pt-0.5">
                      <h3 className="text-[20px] font-bold text-foreground-950 mb-1 group-hover:text-accent-700 transition-colors">
                        {factor.title}
                      </h3>
                      <p className={`text-[15px] font-semibold mb-3 ${colorClass(barColor as ColorKey, 'text')}`}>
                        {factor.tagline}
                      </p>
                      <p className="text-[14px] text-foreground-600 leading-relaxed max-w-[600px]">
                        {factor.description}
                      </p>
                    </div>
                    {/* Right: stat */}
                    <div className="flex-shrink-0 flex flex-col items-end justify-start pt-0.5 w-[140px]">
                      <div className="text-right">
                        <div className={`text-[38px] font-[800] leading-none tracking-tight mb-1 ${statColorClass(barColor as ColorKey)}`}>
                          {factor.stat}
                        </div>
                        <div className="text-[11px] text-foreground-400 leading-snug max-w-[130px] text-right">
                          {factor.statLabel}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Mobile: accordion ─────────────────────────────────── */}
                <div className="md:hidden">
                  {/* Collapsed header — always visible */}
                  <button
                    onClick={() => handleToggle(i)}
                    className={`w-full flex items-center gap-3 px-4 py-4 text-left transition-colors duration-200 cursor-pointer ${
                      isOpen ? 'bg-background-50' : 'hover:bg-background-50/60'
                    }`}
                  >
                    {/* Impact meter + rank */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <ImpactMeter />
                      <span className="text-[26px] font-[800] text-foreground-200 leading-none tracking-tighter select-none">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                    </div>

                    {/* Title + tagline */}
                    <div className="flex-1 min-w-0">
                      <div className="text-[15px] font-bold text-foreground-950 leading-tight">
                        {factor.title}
                      </div>
                      <div className={`text-[12px] font-semibold mt-0.5 leading-tight ${colorClass(barColor as ColorKey, 'text')}`}>
                        {factor.tagline}
                      </div>
                    </div>

                    {/* Stat pill */}
                    <div className="flex-shrink-0 flex flex-col items-end gap-1">
                      <span className={`text-[18px] font-[800] leading-none ${statColorClass(barColor as ColorKey)}`}>
                        {factor.stat}
                      </span>
                      <div className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
                        <i className="ri-arrow-down-s-line text-[16px] text-foreground-400 w-4 h-4 flex items-center justify-center" />
                      </div>
                    </div>
                  </button>

                  {/* Expanded description */}
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      isOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-4 pb-4 pt-1">
                      {/* Stat label full */}
                      <div className="mb-3">
                        <span className={`text-[11px] font-medium ${colorClass(barColor as ColorKey, 'text')} opacity-70`}>
                          {factor.statLabel}
                        </span>
                      </div>
                      <p className="text-[13px] text-foreground-600 leading-relaxed">
                        {factor.description}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom editorial callout */}
        <div className="mt-10 md:mt-14 bg-white rounded-card border border-background-200 overflow-hidden">
          <div className="p-5 md:p-10">
            {/* Headline row */}
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-5 mb-6 md:mb-8">
              <div className="max-w-[480px]">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-50 text-accent-700 text-[11px] font-bold tracking-widest uppercase mb-3 md:mb-4">
                  Reality Check
                </span>
                <h3 className="text-[20px] md:text-[28px] font-[750] leading-[1.2] text-foreground-950 tracking-tight">
                  The spike trap:
                  <br />
                  <span className="text-accent-500">volatility doesn't wait</span>
                  <br />
                  for your confirmation
                </h3>
              </div>

              {/* Stat burst — mobile compact, desktop large */}
              <div className="flex-shrink-0 flex items-center gap-3 md:gap-8">
                <div className="text-center">
                  <div className="text-[36px] md:text-[56px] font-[800] leading-none text-foreground-200 tracking-tighter">
                    1.2
                  </div>
                  <div className="text-[10px] md:text-[11px] text-foreground-400 mt-1 font-medium leading-tight">
                    Normal spread
                    <br />
                    EURUSD (pips)
                  </div>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <div className="text-[20px] md:text-[28px] text-foreground-300 font-[300] leading-none">→</div>
                  <span className="text-[9px] text-foreground-300 font-medium uppercase tracking-wide">spike</span>
                </div>
                <div className="text-center">
                  <div className="text-[36px] md:text-[56px] font-[800] leading-none text-accent-500 tracking-tighter">
                    15
                  </div>
                  <div className="text-[10px] md:text-[11px] text-foreground-400 mt-1 font-medium leading-tight">
                    NFP spike
                    <br />
                    within 3 seconds
                  </div>
                </div>
              </div>
            </div>

            {/* Two column explainer */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-10">
              {/* Left: Why */}
              <div>
                <div className="flex items-center gap-2 mb-2 md:mb-3">
                  <span className="w-1 h-4 rounded-full bg-foreground-400" />
                  <h4 className="text-[12px] md:text-[13px] font-bold text-foreground-600 tracking-widest uppercase">
                    Why It Happens
                  </h4>
                </div>
                <p className="text-[13px] md:text-[14px] text-foreground-600 leading-relaxed">
                  Market makers don&rsquo;t widen spreads out of greed. During high-impact events like Non-Farm Payroll, CPI, or FOMC, the risk of being picked off during extreme uncertainty is too high — so they pull quotes entirely or price in a heavy safety margin. The result: your 1.2 pip spread detonates to 8–15 pips in under three seconds. No warning, no negotiation.
                </p>
              </div>

              {/* Right: What to do */}
              <div>
                <div className="flex items-center gap-2 mb-2 md:mb-3">
                  <span className="w-1 h-4 rounded-full bg-accent-400" />
                  <h4 className="text-[12px] md:text-[13px] font-bold text-foreground-600 tracking-widest uppercase">
                    What To Do
                  </h4>
                </div>
                <div className="space-y-2.5 md:space-y-3">
                  {[
                    'Check the economic calendar before every session — know exactly when high-impact news drops.',
                    'Avoid entering new positions within 5 minutes of red-tier events. The spread you see pre-release is not the spread you get at execution.',
                    'If you must trade news, use limit orders — never market orders. A slipped market order during a spike can cost you more in spread than the trade itself.',
                  ].map((text, idx) => (
                    <div key={idx} className="flex items-start gap-2.5 md:gap-3">
                      <span className="flex-shrink-0 mt-0.5 text-[11px] md:text-[12px] font-[700] text-accent-500">
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                      <p className="text-[13px] md:text-[14px] text-foreground-600 leading-relaxed">{text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Bottom accent line */}
            <div className="mt-6 md:mt-8 pt-5 md:pt-6 border-t border-background-200">
              <p className="text-[11px] md:text-[12px] text-foreground-400 text-center leading-relaxed max-w-[560px] mx-auto">
                These numbers are based on real market behaviour during major news events. Actual spread widening varies by broker model, liquidity provider depth, and order book pressure at the moment of release.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}